import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

export interface RunActionResult {
    success: boolean;
    message: string;
    resultCode: string;
}

// Lazily-populated cache of action Name → ID. Action IDs are stable in the
// metadata layer, so resolving them once per browser session is safe and
// avoids an N+1 RunView for every UI action call.
const actionIdCache = new Map<string, string>();

async function resolveActionId(actionName: string): Promise<string | null> {
    const cached = actionIdCache.get(actionName);
    if (cached) return cached;
    const rv = new RunView();
    const actionResult = await rv.RunView({
        EntityName: 'MJ: Actions',
        ExtraFilter: `Name='${actionName.replace(/'/g, "''")}'`,
        MaxRows: 1,
    });
    if (!actionResult.Success || !actionResult.Results?.length) return null;
    const id = actionResult.Results[0].ID as string;
    actionIdCache.set(actionName, id);
    return id;
}

/**
 * Invoke an MJ Action via GraphQL RunAction mutation.
 *
 * Returns Success/Message/ResultCode. Some Marketing actions encode JSON in
 * the Message field for read-via-action calls (Recommend Decision,
 * Get Sprint Slot Capacity, Suggest Experiment Tier) — callers can
 * `JSON.parse(result.message)` after success.
 */
export async function runAction(actionName: string, params: { Name: string; Value: string }[]): Promise<RunActionResult> {
    try {
        const gql = Metadata.Provider as GraphQLDataProvider;
        const actionId = await resolveActionId(actionName);
        if (!actionId) {
            return { success: false, message: `Action '${actionName}' not found`, resultCode: 'NOT_FOUND' };
        }
        const result = await gql.ExecuteGQL(
            `mutation RunAction($input: RunActionInput!) {
                RunAction(input: $input) { Success Message ResultCode }
            }`,
            {
                input: {
                    ActionID: actionId,
                    Params: params.map(p => ({ Name: p.Name, Value: p.Value, Type: 'Input' })),
                },
            }
        );
        const actionResponse = result?.RunAction;
        return {
            success: !!actionResponse?.Success,
            message: actionResponse?.Message || 'Unknown error',
            resultCode: actionResponse?.ResultCode || 'UNKNOWN',
        };
    } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        LogError(`[runAction] '${actionName}' failed: ${msg}`);
        return { success: false, message: msg, resultCode: 'ERROR' };
    }
}

/**
 * Try to parse JSON embedded in an action's message and validate it through
 * the caller-supplied type guard. Returns null on parse failure or when the
 * guard rejects the parsed value. Forces callers to do runtime validation
 * rather than relying on a lying `as T` cast.
 */
export function parseActionMessageJson<T>(message: string, guard: (v: unknown) => v is T): T | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(message);
    } catch {
        return null;
    }
    return guard(parsed) ? parsed : null;
}

/** Shared shape for Recommend Decision JSON payloads embedded in action Message. */
export interface RecommendDecisionResp {
    decision?: string;
    rationale?: string;
    confidence?: string;
    nextActionHint?: string;
}

/** Runtime guard for RecommendDecisionResp — accepts any object with at least one expected string field. */
export function isRecommendDecisionResp(v: unknown): v is RecommendDecisionResp {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    const optStr = (k: string) => r[k] === undefined || typeof r[k] === 'string';
    return optStr('decision') && optStr('rationale') && optStr('confidence') && optStr('nextActionHint');
}

/**
 * Recommend Decision returns plain text in Message (e.g. "Recommend GRADUATE (High).")
 * while peer actions embed JSON. Until that contract is normalized, callers
 * can route the message through this extractor to pull a usable decision +
 * confidence from the plain string.
 */
export function extractRecFromMessage(message: string): { decision: string; confidence: string; rationale: string } | null {
    if (!message) return null;
    const m = /Recommend\s+(KEEP|ITERATE|KILL|GRADUATE)(?:\s*\((High|Medium|Low)\))?/i.exec(message);
    if (!m) return null;
    return {
        decision: m[1].toUpperCase(),
        confidence: m[2] ? m[2][0].toUpperCase() + m[2].slice(1).toLowerCase() : '',
        rationale: message,
    };
}
